import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { PublicAnnotation, PublicComment, PublicReviewAsset, PublicReviewer, ReviewCreateCommentRequest } from '../../../shared/types';
import { formatReviewTime, reviewPrimaryButton, reviewQuietButton } from './ReviewUi';

interface CommentsPanelProps {
  comments: PublicComment[];
  annotations: PublicAnnotation[];
  reviewers: PublicReviewer[];
  reviewer: PublicReviewer | null;
  assets: PublicReviewAsset[];
  activeAsset: PublicReviewAsset | null;
  getCurrentTime: () => number;
  targetAnnotation: PublicAnnotation | null;
  onClearTarget: () => void;
  onAdd: (request: ReviewCreateCommentRequest) => Promise<void>;
  onDelete: (comment: PublicComment) => Promise<void>;
  onSeek: (assetId: string, time: number) => void;
}

const commentClock = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' });
const commentDay = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' });
const commentDayYear = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
const commentFull = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export interface CommentDate {
  short: string;
  full: string;
}

function dayStart(value: Date): number {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
}

/**
 * Short in the list, exact on hover. Today reads as a time, yesterday needs no
 * time at all, and anything older falls back to a date — with the year only
 * once it stops being obvious.
 */
export function formatCommentDate(createdAt: number, now: number = Date.now()): CommentDate {
  const created = new Date(createdAt);
  const today = new Date(now);
  // Narrow no-break spaces travel badly through title attributes.
  const full = commentFull.format(created).replace(/ /g, ' ');
  const days = Math.round((dayStart(today) - dayStart(created)) / 86_400_000);
  if (days <= 0) {
    const time = commentClock.format(created).replace(/ /g, ' ').toLocaleLowerCase('en-AU');
    return { short: `Today, ${time}`, full };
  }
  if (days === 1) return { short: 'Yesterday', full };
  const formatter = created.getFullYear() === today.getFullYear() ? commentDay : commentDayYear;
  return { short: formatter.format(created), full };
}

export default function CommentsPanel({ comments, annotations, reviewers, reviewer, assets, activeAsset, getCurrentTime, targetAnnotation, onClearTarget, onAdd, onDelete, onSeek }: CommentsPanelProps) {
  const [body, setBody] = useState('');
  const [atTime, setAtTime] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [busyAction, setBusyAction] = useState<'save' | string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reviewerById = useMemo(() => new Map(reviewers.map((item) => [item.id, item])), [reviewers]);
  const assetById = useMemo(() => new Map(assets.map((item) => [item.id, item])), [assets]);
  const annotationById = useMemo(() => new Map(annotations.map((item) => [item.id, item])), [annotations]);

  // Arriving from a transcript annotation should land on a ready composer,
  // not on a closed one-liner.
  useEffect(() => {
    if (targetAnnotation) setExpanded(true);
  }, [targetAnnotation]);

  // Playback time is deliberately kept out of ReviewPage's render loop, so the
  // one place that must show a live clock samples it itself, and only while the
  // composer is open offering to attach it. The label and the saved time_s then
  // agree instead of drifting apart by however long since the last re-render.
  const showsPlaybackTime = expanded && !targetAnnotation && activeAsset !== null;
  const [labelTime, setLabelTime] = useState(0);
  useEffect(() => {
    if (!showsPlaybackTime) return;
    setLabelTime(getCurrentTime());
    const tick = window.setInterval(() => setLabelTime(getCurrentTime()), 250);
    return () => window.clearInterval(tick);
  }, [getCurrentTime, showsPlaybackTime]);

  function collapse() {
    setExpanded(false);
    setAtTime(false);
    onClearTarget();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyAction('save');
    setError(null);
    try {
      await onAdd({
        body,
        ...(targetAnnotation ? { annotation_id: targetAnnotation.id, asset_id: targetAnnotation.asset_id } : {}),
        ...(!targetAnnotation && atTime && activeAsset ? { asset_id: activeAsset.id, time_s: getCurrentTime() } : {}),
      });
      setBody('');
      collapse();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'We could not save that comment. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  async function remove(comment: PublicComment) {
    setBusyAction(comment.id);
    setError(null);
    try {
      await onDelete(comment);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'We could not delete that comment. Mind trying again?');
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <section aria-labelledby="comments-title" className="rounded-card border border-line bg-card p-5 shadow-card">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="comments-title" className="title-section">Comments</h2>
        <span className="font-mono text-[11px] text-faint">{comments.length}</span>
      </div>
      <form className="mt-3" onSubmit={submit}>
        {targetAnnotation ? <div className="mb-2 flex items-center justify-between gap-3 rounded-input bg-accent-tint px-3 py-2 text-xs text-accent"><span>Commenting on a transcript annotation</span><button type="button" className="min-h-11 px-2 font-semibold" onClick={onClearTarget}>Clear</button></div> : null}
        {expanded ? (
          <>
            <label className="sr-only" htmlFor="comment-body">Add a comment</label>
            <textarea id="comment-body" className="anno-field min-h-20" autoFocus value={body} onChange={(event) => setBody(event.target.value)} placeholder="What should we know?" required />
            {!targetAnnotation && activeAsset ? <label className="mt-2 flex min-h-11 items-center gap-2.5 text-[13px] text-muted"><input type="checkbox" className="h-5 w-5 accent-accent" checked={atTime} onChange={(event) => setAtTime(event.target.checked)} />Attach to {formatReviewTime(labelTime)} in {activeAsset.title}</label> : null}
            <div className="mt-2 flex justify-end gap-1">
              <button type="button" className={reviewQuietButton} onClick={() => { setBody(''); collapse(); }}>Cancel</button>
              <button type="submit" className={`${reviewPrimaryButton} min-h-11 px-4`} disabled={busyAction !== null || !body.trim()} aria-busy={busyAction === 'save' || undefined}>{busyAction === 'save' ? <><span className="inline-spinner" aria-hidden="true" />Posting</> : 'Post'}</button>
            </div>
          </>
        ) : (
          <div className="cc-input">
            <input className="cc-input-field" value={body} onChange={(event) => setBody(event.target.value)} onFocus={() => setExpanded(true)} placeholder="Add a comment…" aria-label="Add a comment" />
            <button type="button" className={`${reviewQuietButton} min-h-9 px-3 text-[13px]`} onClick={() => setExpanded(true)}>Post</button>
          </div>
        )}
        {error ? <p className="mt-2 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">{error}</p> : null}
      </form>
      {comments.length === 0 ? <p className="py-6 text-center text-sm text-muted">No comments yet. Questions and notes will stay together here.</p> : (
        <ol className="mt-3">
          {comments.map((comment) => {
            const author = reviewerById.get(comment.reviewer_id);
            const asset = comment.asset_id ? assetById.get(comment.asset_id) : undefined;
            const annotation = comment.annotation_id ? annotationById.get(comment.annotation_id) : undefined;
            // A toolbar comment carries no time of its own; its annotation has one.
            const anchorTime = comment.time_s ?? annotation?.start_s ?? null;
            const date = formatCommentDate(comment.created_at);
            return (
              <li key={comment.id} className="cc-item">
                <div className="cc-head">
                  <span className="cc-name">{author?.display_name ?? 'Reviewer'}</span>
                  <time className="cc-when" dateTime={new Date(comment.created_at).toISOString()} title={date.full}>{date.short}</time>
                  {reviewer?.id === comment.reviewer_id ? <button type="button" className="cc-delete" onClick={() => void remove(comment)} disabled={busyAction !== null} aria-busy={busyAction === comment.id || undefined}>{busyAction === comment.id ? 'Deleting' : 'Delete'}</button> : null}
                  {asset && anchorTime !== null
                    ? <button type="button" className="cc-anchor" onClick={() => onSeek(asset.id, anchorTime)} title={`Jump to ${asset.title} at ${formatReviewTime(anchorTime)}`}>{asset.title} · {formatReviewTime(anchorTime)}</button>
                    : asset ? <span className="cc-anchor">{asset.title}</span> : <span className="cc-note">Project note</span>}
                </div>
                <p className="cc-body">{comment.body}</p>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
